import ContainerLayout from '@/shared/components/ContainerLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Shield, AlertCircle, CheckCircle, FileText, Users, Settings, Monitor, ExternalLink, HelpCircle } from 'lucide-react';
import { ROUTES } from '@/config/routeConfig';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table";

const SLAPage = () => {
  return (
    <ContainerLayout shouldShowSidebar={false} shouldAllowRegionChange={false} shouldAllowAccountChange={false}>
      <div className="flex flex-col flex-1 p-4 space-y-4">
        {/* Header Section */}
        <div className="glass-panel p-4 rounded-lg">
          <h1 className="text-3xl font-bold">Orbius Service Level Agreement</h1>
          <p className="text-lg">
            Direct technical assistance and support guidelines for Orbius customers
          </p>
        </div>

        {/* Support Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-500" />
                <CardTitle className="text-lg">Customer Focus</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-bold text-blue-500 mb-1">Direct Support</div>
              <p className="text-sm">Technical assistance for customers only</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-green-500" />
                <CardTitle className="text-lg">Support Model</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-bold text-green-500 mb-1">B2B Only</div>
              <p className="text-sm">Strictly business-to-business support</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-orange-500" />
                <CardTitle className="text-lg">Escalation</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-bold text-orange-500 mb-1">Starlink</div>
              <p className="text-sm">Level 2 & 3 escalation partner</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>What Orbius Support Includes</CardTitle>
                <CardDescription>Direct technical assistance provided to customers (not end-users)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Terminal provisioning and lifecycle management</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Service line monitoring and status alerts</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Configuration of Orbius API interfaces</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Usage of the Orbius Customer Portal</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Troubleshooting connectivity issues isolated to the Orbius-supplied Services or Starlink equipment</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>What Support Does NOT Include</CardTitle>
                <CardDescription>Items outside the scope of Orbius customer support</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <span>Network hardware not provided under the Agreement (e.g., third-party routers)</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <span>Third-party software</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <span>Internal LAN issues</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Customer Obligations Before Contacting Support</CardTitle>
                <CardDescription>Required steps before raising a support ticket</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Settings className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span>Verify that the issue is not due to local routers, power, or connectivity outside of the Orbius solution</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Monitor className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span>Consolidate logs or fault details before raising a ticket</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <FileText className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span>Include service line ID, terminal serial number, and impact description</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Escalation Pathways</CardTitle>
                <CardDescription>How Level 2 and Level 3 support requests are handled</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-orange-50 dark:bg-orange-950/20 rounded-lg">
                  <p className="text-sm mb-3">
                    All Level 2 and Level 3 support requests are escalated from Orbius to <strong>Starlink</strong> under our Authorized Reseller Agreement.
                  </p>
                  <div className="space-y-2">
                    <h5 className="font-semibold text-sm">These include:</h5>
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                        <span>Firmware bugs</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                        <span>Terminal hardware failures</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                        <span>Network routing and core satellite issues</span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2 mt-4">
                    <p className="text-sm">
                      Orbius serves as your coordination and tracking partner, ensuring full visibility until closure.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Service Level Agreement Tiers</CardTitle>
                <CardDescription>Enterprise support levels designed for B2B operational needs</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm">
                  Orbius offers two levels of enterprise support designed to meet the operational needs of our B2B clients.
                  All support is delivered via the Orbius Customer Portal, which serves as the central hub for ticket submission,
                  status tracking, and service management.
                </p>

                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                    <h5 className="font-semibold text-blue-500 dark:text-blue-100 mb-2">Standard Support</h5>
                    <p className="text-sm text-blue-500 dark:text-blue-200">
                      Designed for clients with predictable support needs during UTC business hours. It includes access to
                      technical assistance, ticket escalation, and coordination with Starlink for underlying issues.
                    </p>
                  </div>

                  <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-lg">
                    <h5 className="font-semibold text-green-500 dark:text-green-100 mb-2">Extended Support</h5>
                    <p className="text-sm text-green-500 dark:text-green-200">
                      Tailored for mission-critical deployments requiring extended support hours, faster response time,
                      including priority handling of Level 1 incidents, and continuous monitoring through the Orbius platform.
                    </p>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <Table className="w-full text-sm border-collapse">
                    <TableHeader className="bg-muted">
                      <TableRow className="border-b">
                        <TableHead className="text-left p-3 font-semibold">Support Tier</TableHead>
                        <TableHead className="text-left p-3 font-semibold">Support Hours</TableHead>
                        <TableHead className="text-left p-3 font-semibold">Initial Response Time</TableHead>
                        <TableHead className="text-left p-3 font-semibold">Resolution Time for Level 1 Issues</TableHead>
                        <TableHead className="text-left p-3 font-semibold">Level 2 and 3 Escalation</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow className="border-b">
                        <TableCell className="p-3 font-medium">Standard</TableCell>
                        <TableCell className="p-3">9:00 AM – 5:00 PM UTC (Mon–Fri), excl. US holidays</TableCell>
                        <TableCell className="p-3">Within 8 hours</TableCell>
                        <TableCell className="p-3">Best effort within 2 business days</TableCell>
                        <TableCell className="p-3">Escalated to Starlink<br /><span className="text-xs">(typically, 2-3 business days)</span></TableCell>
                      </TableRow>
                      <TableRow className="border-b">
                        <TableCell className="p-3 font-medium">Extended</TableCell>
                        <TableCell className="p-3">9:00 AM – 10:00 PM UTC (Mon–Fri), excl. US holidays</TableCell>
                        <TableCell className="p-3">Within 4 hours</TableCell>
                        <TableCell className="p-3">Same calendar day</TableCell>
                        <TableCell className="p-3">Escalated to Starlink<br /><span className="text-xs">(typically, 2-3 business days)</span></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                <div className="space-y-3">
                  <h5 className="font-semibold text-sm">Relevant Links:</h5>
                  <div className="space-y-2">
                    <a href="https://www.starlink.com/legal/documents/DOC-1728-44881-79" target="_blank" className="flex items-center gap-2 text-sm text-primary hover:underline">
                      <ExternalLink className="h-4 w-4" />
                      Priority Services Plan
                    </a>
                    <a href="https://www.starlink.com/legal/documents/DOC-1729-34307-72" target="_blank" className="flex items-center gap-2 text-sm text-primary hover:underline">
                      <ExternalLink className="h-4 w-4" />
                      Priority Plan Service Level Agreement
                    </a>
                    <a href="https://www.starlink.com/legal/documents/DOC-1470-99699-90?regionCode=IS" target="_blank" className="flex items-center gap-2 text-sm text-primary hover:underline">
                      <ExternalLink className="h-4 w-4" />
                      Starlink specifications
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Help Center Call to Action */}
            <Card className="bg-primary/5 border-primary/20">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <HelpCircle className="h-5 w-5 text-primary" />
                  <CardTitle>Need Additional Help?</CardTitle>
                </div>
                <CardDescription>Access our comprehensive support resources and submit tickets</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm mb-4">
                  Visit our Help Center for detailed documentation, troubleshooting guides, and to submit support tickets when you've completed the pre-contact requirements above.
                </p>
                <Button
                  onClick={() => window.open(ROUTES.HELP_CENTER, '_blank')}
                  className="gap-2"
                >
                  <HelpCircle className="h-4 w-4" />
                  Visit Help Center
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Footer Notice */}
        <Card className="mt-4">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold mb-2">Important Notice</h4>
                <p className="text-sm">
                  Orbius support is strictly B2B. Customers are responsible for first-level triage of all issues
                  reported by their users. Only once an issue has been reasonably isolated to
                  Orbius-provided Terminals, Services, or software should a support ticket be raised.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </ContainerLayout>
  );
};

export default SLAPage; 